// Wraps React Flow with our custom nodes/edges, Dagre layout, and
// pan/zoom controls. The Architecture document is the source of truth — this
// component is a pure VIEW derived from it via a memoized selector.

import { useEffect, useMemo, useRef } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import StructuralNode from "./StructuralNode";
import ResourceNode from "./ResourceNode";
import { computeLayout, structuralEdges, NODE_SIZE } from "../../lib/dagreLayout";
import { canNest, descendantIds, indexChildren } from "../../lib/archModel";

const NODE_TYPES = {
  structural: StructuralNode,
  resource: ResourceNode,
};

/**
 * Derive the visible React Flow node/edge arrays from the Architecture document
 * and the editing state. The output is a PURE function of inputs; this is what
 * lets us re-run dagre only when the structural shape changes.
 */
function buildFlow({
  archNodes,
  peerConnections,
  showAddressing,
  collapsedIds,
  selectedId,
  draggingPayload,
  pendingPlacement,
  peerConnectMode,
  provider,
}) {
  const childMap = indexChildren(archNodes);

  // Hide subtrees under collapsed structural nodes.
  const hidden = new Set();
  for (const cid of collapsedIds) {
    descendantIds(archNodes, cid).forEach((d) => hidden.add(d));
  }
  const visibleNodes = archNodes.filter((n) => !hidden.has(n.id));
  const edges = structuralEdges(visibleNodes);
  const positions = computeLayout(visibleNodes, edges);

  // Pre-compute "is this node a valid drop target?" for the currently dragging
  // payload, and "is this node a valid placement target?" for click-to-place.
  const dragPayload = draggingPayload || null;
  const placementPayload = pendingPlacement || null;

  const computeFlags = (n) => {
    const isStructural = n.kind === "structural";
    let validDropTarget = false;
    let pendingPlacementValid = false;
    let dimmed = false;
    let peerSource = false;
    let peerTargetCandidate = false;

    if (dragPayload && isStructural) {
      if (dragPayload.kind === "new-resource") {
        validDropTarget = canNest(provider, dragPayload.tier || "compute", n.type, "resource");
      } else if (dragPayload.kind === "move-node") {
        // Determine the kind of the source node from its id.
        const source = archNodes.find((x) => x.id === dragPayload.id);
        if (source) {
          const descSet = new Set(descendantIds(archNodes, source.id));
          if (source.id !== n.id && !descSet.has(n.id)) {
            validDropTarget = canNest(provider, source.type, n.type, source.kind);
          }
        }
      }
    }
    if (placementPayload && isStructural) {
      if (placementPayload.kind === "new-resource") {
        pendingPlacementValid = canNest(provider, placementPayload.tier || "compute", n.type, "resource");
      }
    }

    if (peerConnectMode?.sourceId) {
      if (peerConnectMode.sourceId === n.id) peerSource = true;
      else peerTargetCandidate = true;
    }

    // Dim non-target nodes during an active placement/peer-mode for visual focus.
    if (placementPayload && !pendingPlacementValid) dimmed = true;
    if (peerConnectMode?.sourceId && !peerSource && !peerTargetCandidate) dimmed = true;

    return { validDropTarget, pendingPlacementValid, peerSource, peerTargetCandidate, dimmed };
  };

  const rfNodes = visibleNodes.map((n) => {
    const size =
      n.kind === "structural" ? NODE_SIZE.STRUCTURAL_SIZE : NODE_SIZE.RESOURCE_SIZE;
    const pos = positions.get(n.id) || { x: 0, y: 0, ...size };
    const flags = computeFlags(n);
    return {
      id: n.id,
      type: n.kind === "structural" ? "structural" : "resource",
      position: { x: pos.x, y: pos.y },
      // Declared up-front from the same constants dagre laid out with. React
      // Flow only writes measured sizes back onto the nodes array through
      // `onNodesChange`, which this canvas deliberately doesn't use (the
      // Architecture document is the source of truth, not React Flow's store).
      // Without a size here every node looks dimensionless to <MiniMap />, which
      // then skips it and renders an empty box.
      width: pos.width,
      height: pos.height,
      data: {
        name: n.name,
        type: n.type,
        config: n.config,
        showAddressing,
        childCount: (childMap.get(n.id) || []).length,
        collapsed: collapsedIds.has(n.id),
        selected: selectedId === n.id,
        ...flags,
      },
      draggable: false,
      selectable: true,
    };
  });

  const rfEdges = edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: "smoothstep",
    style: {
      stroke: "color-mix(in oklab, var(--color-border-primary) 100%, transparent)",
      strokeWidth: 1.5,
    },
    animated: false,
    selectable: false,
  }));

  const peerEdges = (peerConnections || [])
    .filter((p) => !hidden.has(p.sourceId) && !hidden.has(p.targetId))
    .map((p) => ({
      id: `peer-${p.id}`,
      source: p.sourceId,
      target: p.targetId,
      type: "smoothstep",
      label: p.label || "connects to",
      labelStyle: {
        fill: "var(--color-text-secondary)",
        fontSize: 11,
        fontFamily: "JetBrains Mono, monospace",
      },
      labelBgStyle: {
        fill: "var(--color-background-secondary)",
        stroke: "var(--color-border-primary)",
      },
      labelBgPadding: [6, 4],
      labelBgBorderRadius: 6,
      style: {
        stroke: "var(--color-accent)",
        strokeWidth: 1.5,
        strokeDasharray: "6 4",
        opacity: 0.85,
      },
      animated: false,
      selectable: false,
    }));

  return { rfNodes, rfEdges: [...rfEdges, ...peerEdges] };
}

function CanvasInner({
  nodes,
  peerConnections,
  collapsedIds,
  onToggleCollapse,
  onNodeClick,
  onNodeContextMenu,
  onDropPayload,
  selectedId,
  focusTarget,
  onAfterFocus,
  draggingPayload,
  pendingPlacement,
  peerConnectMode,
  provider,
  invalidNodeIds,
  showAddressing = false,
}) {
  const { rfNodes, rfEdges } = useMemo(
    () =>
      buildFlow({
        archNodes: nodes,
        peerConnections,
        showAddressing,
        collapsedIds,
        selectedId,
        draggingPayload,
        pendingPlacement,
        peerConnectMode,
        provider,
      }),
    [
      nodes,
      peerConnections,
      showAddressing,
      collapsedIds,
      selectedId,
      draggingPayload,
      pendingPlacement,
      peerConnectMode,
      provider,
    ]
  );

  // Inject event handlers into each node's data prop. The actual node
  // components read them off `data` (see StructuralNode.onDrop etc).
  const invalidSet = useMemo(
    () => new Set(Array.isArray(invalidNodeIds) ? invalidNodeIds : []),
    [invalidNodeIds]
  );
  const nodesWithHandlers = useMemo(
    () =>
      rfNodes.map((n) => ({
        ...n,
        data: {
          ...n.data,
          onToggleCollapse,
          onDrop: onDropPayload,
          onContextMenu: onNodeContextMenu,
          invalid: invalidSet.has(n.id),
        },
      })),
    [rfNodes, onToggleCollapse, onDropPayload, onNodeContextMenu, invalidSet]
  );

  const rf = useReactFlow();

  // Recentre only when the focus TARGET changes. The deps list is complete (so
  // react-hooks/exhaustive-deps is satisfied), but rfNodes changes on every edit
  // and `rf`/`onAfterFocus` are unstable identities — without this guard the
  // camera would snap back mid-edit and fight the user's panning.
  const lastFocusRef = useRef(null);
  useEffect(() => {
    if (!focusTarget) {
      // Reset so re-focusing the same node later still centres.
      lastFocusRef.current = null;
      return;
    }
    if (lastFocusRef.current === focusTarget) return;
    lastFocusRef.current = focusTarget;

    const node = rfNodes.find((n) => n.id === focusTarget);
    if (node) {
      rf.setCenter(
        node.position.x + (node.type === "structural" ? 140 : 110),
        node.position.y + (node.type === "structural" ? 42 : 38),
        { zoom: 1.1, duration: 600 }
      );
    }
    onAfterFocus?.();
  }, [focusTarget, rfNodes, rf, onAfterFocus]);

  return (
    <ReactFlow
      nodes={nodesWithHandlers}
      edges={rfEdges}
      nodeTypes={NODE_TYPES}
      onNodeClick={(_e, n) => onNodeClick?.(n.id)}
      onNodeContextMenu={(e, n) => {
        if (onNodeContextMenu) {
          e.preventDefault();
          onNodeContextMenu(n.id, e.clientX, e.clientY);
        }
      }}
      fitView
      fitViewOptions={{ padding: 0.2, includeHiddenNodes: false }}
      minZoom={0.2}
      maxZoom={1.8}
      panOnDrag
      zoomOnScroll
      proOptions={{ hideAttribution: true }}
      data-testid="arch-flow-canvas"
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={24}
        size={1.2}
        color="color-mix(in oklab, var(--color-border-primary) 60%, transparent)"
      />
      {/* Both overlays are themed through React Flow's own `--xy-*` custom
          properties (see arch-canvas-overlays in index.css) rather than the
          `style` prop: `style` only lands on the outer container, so the
          Controls *buttons* kept React Flow's light defaults — a white button
          with `color: inherit`, which in our dark UI meant white icons on a
          white square. */}
      <Controls position="bottom-right" showInteractive={false} />
      <MiniMap
        pannable
        zoomable
        position="bottom-left"
        nodeColor={(n) => (n.type === "structural" ? "#0EA5E9" : "#0D9488")}
        nodeStrokeWidth={0}
        // Outlines the current viewport inside the minimap. Passed as props
        // rather than CSS because React Flow scales the width by the minimap's
        // own zoom factor here — a raw CSS stroke-width would be sub-pixel.
        maskStrokeColor="var(--color-accent)"
        maskStrokeWidth={1}
      />
    </ReactFlow>
  );
}

export default function DiagramCanvas(props) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}
